import React, { useEffect } from 'react';
import { FormLayoutComposer, IFormLayoutProps } from 'components/common/form/renderer/FormLayoutComposer';
import { useEntityIssues } from 'hooks/useEntityIssues';
import IssuesTab from 'app/content-workflow/element-editor/IssuesTab';
import { useStageGateConfig } from 'app/content-workflow/element-editor/StageGateContext';
import { useEditPanelBridge } from 'app/content-workflow/element-editor/EditPanelBridge';
import { useDispatch } from 'react-redux';
import { show, setIssuesTarget, setTargetQuestionId } from 'store/conciergeSlice';
import { Badge } from '@mui/material';
import { useAppSelector } from 'store/hooks';
import { customTabProps } from 'components/common/form/layout/tab';
import { MissingAccess } from 'api/element-issues';

export default function ElementShortForm(props: IFormLayoutProps) {
    const { t } = props;
    const pk = props.elementProps.pk ?? '';
    const match = pk.match(/element-short\/(\d+)\/form/);
    const entityId = match ? Number(match[1]) : 0;

    const dispatch = useDispatch();
    const conciergeEnabled = useAppSelector((state) => state.auth?.conciergeInfo?.enabled);
    const gates = useStageGateConfig();
    const showIssuesTab = Boolean(conciergeEnabled) && (gates.showInconsistencies || gates.showClarifications);
    const issues = useEntityIssues(entityId, 'element', entityId > 0 && showIssuesTab);
    const bridge = useEditPanelBridge();

    // Keep the edit panel footer (Save & Move gating, §6.1) in sync with the live counts
    useEffect(() => {
        const context = issues.context;
        const missingAccess: MissingAccess[] =
            !context || context.isKgManager === true
                ? []
                : context.missingAccess?.length
                  ? context.missingAccess
                  : ['report_edit'];
        bridge?.reportIssueCounts({
            unresolved: issues.unresolvedCount,
            unanswered: issues.unansweredCount,
            missingAccess,
            loaded: Boolean(context),
        });
    }, [bridge, issues.unresolvedCount, issues.unansweredCount, issues.context]);

    const handleOpenConcierge = (questionId?: string) => {
        dispatch(setIssuesTarget({ type: 'element', id: entityId }));
        dispatch(setTargetQuestionId(questionId ?? null));
        dispatch(show());
    };

    const badgeCount =
        (gates.showInconsistencies ? issues.unresolvedCount : 0) +
        (gates.showClarifications ? issues.unansweredCount : 0);
    const issuesLabel = (
        <Badge
            badgeContent={badgeCount > 0 ? badgeCount : undefined}
            color="error"
            sx={{ pr: badgeCount > 0 ? 1.5 : 0 }}
        >
            {t('issues')}
        </Badge>
    );

    const issuesContent = (
        <IssuesTab issues={issues} onOpenConcierge={handleOpenConcierge} {...customTabProps(props, 'issues')} />
    );

    return (
        <FormLayoutComposer
            props={{
                ...props,
                ...(showIssuesTab
                    ? { customTabs: { issues: issuesContent }, customTabLabels: { issues: issuesLabel } }
                    : {}),
            }}
        />
    );
}
